import { useState, useRef } from "react";
import { UIIcon } from "./ui-icon";
import { UIIconName } from "./ui-icon-type";
import styles from "./ui-icon-toolbar.module.css";
import { filterClasses } from "PosterEditor/utils/filter-classes";

export interface UIIconToolbarItem {
  iconName: UIIconName;
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
}

interface UIIconToolbarProps {
  items: UIIconToolbarItem[];
  iconSize?: number;
}

export const UIIconToolbar = ({ items, iconSize = 24 }: UIIconToolbarProps) => {
  return (
    <div className={styles.toolbar}>
      {items.map((item, index) => (
        <UIIconToolbarButton
          key={`${item.iconName}-${index}`}
          item={item}
          iconSize={iconSize}
        />
      ))}
    </div>
  );
};

interface UIIconToolbarButtonProps {
  item: UIIconToolbarItem;
  iconSize: number;
}

const UIIconToolbarButton = ({ item, iconSize }: UIIconToolbarButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    setIsHovered(true);
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, 400);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setShowTooltip(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleClick = () => {
    if (!item.disabled) {
      item.onClick();
    }
  };

  const variant = item.disabled
    ? "REGULAR"
    : isHovered
    ? "HIGHLIGHTED"
    : "REGULAR";

  return (
    <div className={styles.buttonWrapper}>
      <button
        className={filterClasses(
          styles.button,
          item.disabled && styles.disabled
        )}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        disabled={item.disabled}
        style={{
          width: iconSize + 8,
          height: iconSize + 8,
        }}
      >
        <UIIcon iconName={item.iconName} variant={variant} size={iconSize} />
      </button>
      {showTooltip && !item.disabled && (
        <div className={styles.tooltip}>{item.tooltip}</div>
      )}
    </div>
  );
};
